import * as cookie from "cookie";
import * as _ from "lodash";
import * as log from "loglevel";
import * as qs from "qs";
import * as uuidv4 from "uuid/v4";

import { action, autorun, computed, observable, reaction, runInAction } from "mobx";
import { config } from "../config";
import { Agent } from "../services/agent";
import { App } from "./models/app";
import { Project } from "./models/project";
import { AccountRequirements, User } from "./models/user";
import { RootStore } from "./root-store";

export class AuthStore {
	@observable username: string;
	@observable userId: string;
	@observable loggedIn: boolean = false;
	@observable	loading: boolean = false;
	@observable user: User;
	@observable successMessage: string = null;
	@observable errorMessage: string = null;
	@observable requirements: AccountRequirements = 0;
	@observable jit: string = null;
	@observable firstName: string = "";
	@observable lastName: string = "";
	@observable company: string = "";
	@observable impersonateLeaving: boolean = false;

	//private storeName = config.localStoreName;

	constructor(){
		try{

		}
		catch(err){
			log.error(err);
		}

	}

	@action setAuthProps(user: User){
		this.loggedIn = true;
		this.username = user.username;
		this.userId = user.id;
		this.user = user;
		this.requirements = user.requirements;
	}

	@action async loadFullUser(){
		try{
			this.loading = true;
			let full = await Agent.User.full();
			if(!_.isNil(full.userInfo)){
				let userInfo = full.userInfo;
				let firstName = "";
				let lastName = "";
				let company = "";
				if(!_.isNil(userInfo.firstName)){
					firstName = userInfo.firstName;
				}
				if(!_.isNil(userInfo.lastName)){
					lastName = userInfo.lastName;
				}
				if(!_.isNil(userInfo.company)){
					company = userInfo.company;
				}

				console.log(firstName, lastName);
				runInAction(() => {
					this.firstName = firstName;
					this.lastName = lastName;
					this.company = company;
					this.loading = false;
					this.errorMessage = null;
				});
			}
			else{
				runInAction(() => {
					this.firstName = "";
					this.lastName = "";
					this.loading = false;
					this.errorMessage = null;
				});
			}
		}
		catch(err){
			runInAction(() => {
				this.loading = false;
				this.errorMessage = err.message;
				this.successMessage = null;
			});
		}
	}

	@action updateProperty(name: string, value: any){
		this[name] = value;
	}

	@action async saveProfile(){
		try{
			//this.loading = true;
			await Agent.User.saveProfile({
				"firstName": this.firstName,
				"lastName": this.lastName,
				"company": this.company
			});
			runInAction(() => {
			//	this.loading = false;
				this.errorMessage = null;
			});
		}
		catch(err){
			runInAction(() => {
			//	this.loading = false;
				this.errorMessage = err.message;
				this.successMessage = null;
			});
		}
	}

	@action clearUser(){
		this.loggedIn = false;
		this.username = null;
		this.userId = null;
		this.user = null;
	}

	@action async loadAuth(){
		try{
			if(this.getCookie("jst")){
				this.loading = true;
				let user = new User(await Agent.User.get());
				runInAction(() => {
					this.setAuthProps(user);
					this.loading = false;
					this.errorMessage = null;
				});
			}
			else if(this.getUrlToken("jit")){
				this.loading = true;
				let jit = this.getUrlToken("jit");
				this.jit = jit;
				await Agent.Auth.login(null, null);
				let user = new User(await Agent.User.get());
				runInAction(() => {
					this.setAuthProps(user);
					this.loading = false;
					this.errorMessage = null;
				});
			}
		}
		catch(err){
			runInAction(() => {
				this.clearUser();
				//this.errorMessage = err.message; error message suppressed because of confusion
				this.successMessage = null;
				this.loading = false;
			});
		}
	}

	@action async login(username: string, password: string){
		try{
			this.loading = true;
			await Agent.Auth.login(username, password);
			let user = new User(await Agent.User.get());
			runInAction(() => {
				this.setAuthProps(user);
				this.loading = false;
				this.errorMessage = null;
				this.successMessage = null;
			});
		}
		catch(err){
			runInAction(() => {
				this.clearUser();
				this.errorMessage = err.message;
				this.successMessage = null;
				this.loading = false;
			});
		}
	}

	@action async logout(){
		await Agent.Auth.logout();
		runInAction(() => {
			this.clearUser();
			this.errorMessage = null;
			location.hash = "";
			location.search = "";
		});
	}

	@action async logoutAll(){
		await Agent.Auth.logoutAll();
		runInAction(() => {
			this.clearUser();
			this.errorMessage = null;
			location.hash = "";
			location.search = "";
		});
	}

	@action async impersonate(userId){
		try{
			this.loading = true;
			let result = await Agent.Auth.impersonate(userId);
			let user = new User(await Agent.User.get());
			runInAction(() => {
				this.setAuthProps(user);
				this.loading = false;
				this.errorMessage = null;
				this.successMessage = null;
				this.impersonateLeaving = true;
			});
		}
		catch(err){
			runInAction(() => {
				this.clearUser();
				this.loading = false;
				this.errorMessage = err.message;
				this.successMessage = null;
			});
		}
	}

	@action async resetPassword(email: string){
		await Agent.Auth.resetPassword(email);
	}

	@action async changePassword(newPass: string){
		try{
			this.loading = true;
			let result = await Agent.User.changePassword(newPass);
			let user = new User(await Agent.User.get());
			runInAction(() => {
				this.setAuthProps(user);
				this.loading = false;
				this.errorMessage = null;
				this.successMessage = null;
			});
			return true;
		}
		catch(err){
			runInAction(() => {
				this.errorMessage = err.message;
				this.successMessage = null;
				this.loading = false;
			});
			return false;
		}
	}

	@action async completeSetup(firstName: string, lastName: string, company: string){
		try{
			this.loading = true;
			let result = await Agent.User.completeSetup(firstName, lastName, company);

			runInAction(() => {
				this.loading = false;
				this.errorMessage = null;
				this.successMessage = null;
			});
			return true;
		}
		catch(err){
			runInAction(() => {
				this.errorMessage = err.message;
				this.successMessage = null;
				this.loading = false;
			});
			return false;
		}
	}

	@action async optIn(acceptNotification: boolean, acceptMarketing: boolean){
		try{
			this.loading = true;
			let result = await Agent.User.optIn(acceptNotification, acceptMarketing);

			runInAction(() => {
				this.loading = false;
				this.errorMessage = null;
				this.successMessage = null;
			});
			return true;
		}
		catch(err){
			runInAction(() => {
				this.errorMessage = err.message;
				this.successMessage = null;
				this.loading = false;
			});
			return false;
		}
	}

	@action async acceptTerms(accept: boolean){
		try{
			this.loading = true;
			let result = await Agent.User.terms(accept);

			runInAction(() => {
				this.loading = false;
				this.errorMessage = null;
				this.successMessage = null;
			});
			return true;
		}
		catch(err){
			runInAction(() => {
				this.errorMessage = err.message;
				this.successMessage = null;
				this.loading = false;
			});
			return false;
		}
	}

	hasRequirement(req: AccountRequirements){
		return (this.requirements & req) == req;
	}

	@action removeRequirement(requirement: AccountRequirements){
		if(this.hasRequirement(requirement)){
			this.requirements = this.requirements & ~ requirement;
		}
	}

	getUrlToken(name: string){
		let searchParsed = qs.parse(window.location.search);
		let hashParsed = qs.parse(window.location.hash);
		let token: string = null;
		for(let key in searchParsed){
			if(key.indexOf(name) >= 0 && !_.isUndefined(searchParsed[key])){
				token = searchParsed[key];
				break;
			}
		}
		for(let key in hashParsed){
			if(key.indexOf(name) >= 0 && !_.isUndefined(hashParsed[key])){
				token = hashParsed[key];
				break;
			}
		}

		return token;
	}

	getCookie(name: string){
		let cookies = cookie.parse(document.cookie);
		return cookies[name];
	}

	@action async adminReleaseNewTermsAndConditions(){
		this.loading = true;
		await Agent.Auth.releaseNewTermsAndConditions();
		runInAction(() => {
			this.loading = false;
		});
	}
}



// WEBPACK FOOTER //
// ./src/stores/auth-store.ts